import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["longWait"];
  static values = {
    checkPath: String,
    nextPagePath: String,
    failurePath: String,
    timeoutPath: String,
    successStatus: { type: String, default: "done" },
    failureStatus: { type: String, default: "error" },
    timeoutInterval: { type: Number, default: 3000 },
    maxAttempts: { type: Number, default: 40 },
    longWaitDelay: { type: Number, default: 5000 },
  };

  connect() {
    this.stopped = false;
    this.attemptCount = 0;
    this.checkStatus();
    if (this.hasLongWaitTarget) {
      this.longWaitTimer = setTimeout(
        () => this.longWaitTarget.classList.remove("hide"),
        this.longWaitDelayValue,
      );
    }
  }

  disconnect() {
    this.stop();
  }

  stop() {
    this.stopped = true;
    clearTimeout(this.pollTimer);
    clearTimeout(this.longWaitTimer);
  }

  async checkStatus() {
    if (this.stopped) return;

    this.attemptCount++;
    if (this.maxAttemptsValue > 0 && this.attemptCount > this.maxAttemptsValue) {
      if (!this.timeoutPathValue) alert(this.errorMessage());
      this.redirectTo(this.timeoutPathValue || this.failurePathValue);
      return;
    }

    const { status, error, redirectUrl } = await this.fetchStatus();
    if (this.stopped) return;

    if (redirectUrl) {
      this.redirectTo(redirectUrl);
    } else if (status === this.successStatusValue) {
      this.redirectTo(this.nextPagePathValue);
    } else if (status === this.failureStatusValue) {
      if (error) alert(this.errorMessage(error));
      this.redirectTo(this.failurePathValue);
    } else {
      this.pollTimer = setTimeout(() => this.checkStatus(), this.timeoutIntervalValue);
    }
  }

  errorMessage(error) {
    const message = window.I18n["views.shared.status_poll.error"];
    if (!error) return message;

    const details = [].concat(error).map((line) => `- ${line}`).join("\n");
    return `${message}\n\n${details}`;
  }

  async fetchStatus() {
    const response = await fetch(this.checkPathValue, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    }).catch(() => null);
    if (!response || !response.ok) return {};
    if (response.redirected) return { redirectUrl: response.url };
    if (!this.rendersJson(response)) return {};

    const body = await response.json().catch(() => ({}));
    return { status: body.status, error: body.data?.error };
  }

  rendersJson(response) {
    const contentType = response.headers.get("content-type");
    return contentType?.includes("application/json");
  }

  redirectTo(url) {
    this.stop();
    if (url) window.location.href = url;
  }
}
